import PaymentMethod from '@alltrails/analytics/enums/PaymentMethod';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import logProPurchaseBuyNowClicked from '@alltrails/analytics/events/logProPurchaseBuyNowClicked';
import Button from '@alltrails/shared/denali/components/Button';
import PaymentTabs from 'components/PaymentTabs';
import useEnableRecaptcha from 'hooks/useEnableRecaptcha';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { useEffect, useState, ReactNode } from 'react';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { FormattedMessage } from 'react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import logError from 'utils/logError';
import { handlePaymentFormSuccess } from 'utils/Payment';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import { loadAndExecuteRecaptcha } from 'utils/recaptcha_helpers';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import PaymentFinePrint from 'components/PaymentFinePrint';
import { getPriceTotal } from 'utils/PlanUtils';
import * as baseStyles from '../../../../styles/baseStyles.module.scss';
import CouponCode from '../../CouponCode';
import CreditCardForm, { validate } from '../../CreditCardForm/CreditCardForm';
import ExternalPayForm from '../../ExternalPayForm/ExternalPayForm';
import * as formStyles from '../../styles/styles.module.scss';
import Wrapper from '../../Wrapper';
import PlanTypeCard from '../PlanTypeCard';
import * as styles from './styles/styles.module.scss';

const GIFT_CARD = 'GiftCard';

type Props = {
  plan: Plan;
  couponCode?: any;
  couponError?: any;
  currencyCode?: CurrencyCode;
  handleApplyCoupon?: any;
  handleCouponCodeChange?: any;
  handlePayment?: (params: any) => Promise<any>;
  handleRemoveCoupon?: any;
  headText: ReactNode;
  hideFinePrint?: boolean;
  isCouponApplied?: boolean;
  isCouponInputExpanded?: boolean;
  isCouponInputShown?: boolean;
  paymentTypes?: any;
  coupon?: Coupon;
  showPayWithCard?: boolean;
  webProPurchaseLocation?: WebProPurchaseLocation;
  toggleCouponCode?: any;
  recurlyPublicKey: string;
  userId?: number;
  submitText?: string;
};

export default function PaymentForm({
  couponCode,
  currencyCode,
  coupon,
  plan,
  webProPurchaseLocation,
  showPayWithCard,
  paymentTypes,
  hideFinePrint,
  userId,
  submitText,
  isCouponInputShown,
  toggleCouponCode,
  couponError,
  handleRemoveCoupon,
  handleApplyCoupon,
  handlePayment,
  handleCouponCodeChange,
  headText,
  recurlyPublicKey,
  isCouponApplied,
  isCouponInputExpanded
}: Props) {
  const enableRecaptcha = useEnableRecaptcha();
  const locale = useLanguageRegionCode();
  const [applePay, setApplePay] = useState<any>();
  const [paypal, setPaypal] = useState<any>();
  const [serverError, setServerError] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);

  const [expiration, setExpiration] = useState('');
  const [number, setNumber] = useState('');
  const [cvv, setCvv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [name, setName] = useState('');

  const [activePaymentTab, setActivePaymentTab] = useState(0);

  const [nameError, setNameError] = useState('');
  const [numberError, setNumberError] = useState<ReactNode>();
  const [cvvError, setCvvError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState();
  const [expirationError, setExpirationError] = useState<ReactNode>();

  useEffect(() => {
    function setupApplePay() {
      let applePay;
      if (paymentTypes[2].showTab) {
        applePay = recurly.ApplePay({
          country: 'US', // this is the merchant's country code, so will remain US
          currency: currencyCode,
          label: 'AllTrails Pro',
          total: String(getPriceTotal(plan, coupon))
        });

        applePay.on('error', error => {
          logError(error);
        });

        applePay.on('token', token => {
          loadRecaptchaAndSubmit(undefined, undefined, token.id);
        });
      }

      setApplePay(applePay);
    }

    function setupThirdPartyPayments() {
      setupApplePay();

      const paypal = recurly.PayPal({
        display: {
          displayName: 'AllTrails Pro',
          // @ts-ignore
          amount: getPriceTotal(plan, coupon)
        }
      });

      paypal.on('error', error => {
        logError(error);
      });

      paypal.on('token', token => {
        loadRecaptchaAndSubmit(undefined, token.id);
      });

      setPaypal(paypal);
    }

    if (paymentTypes[1].showTab && typeof recurly !== 'undefined') {
      recurly.configure(recurlyPublicKey);
      setupThirdPartyPayments();
    }
  }, [recurlyPublicKey, coupon]);

  function handleCreditCardFormChange(event: any) {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, {});

    switch (name) {
      case 'number':
        setNumber(value);
        break;
      case 'expiration':
        setExpiration(value);
        break;
      case 'cvv':
        setCvv(value);
        setCvvError(payload.errors.cvv);
        break;
      case 'postalCode':
        setPostalCode(value);
        setPostalCodeError(payload.errors.postalCode);
        break;
      case 'name':
        setName(value);
        setNameError(payload.errors.name);
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  }

  function handleApplePayClick() {
    if (!applePay) {
      return;
    }

    applePay.begin();
  }

  function handlePaypalClick() {
    if (!paypal) {
      return;
    }

    paypal.start();
  }

  function handlePaymentFormError(err: any) {
    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText);
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        setServerError(errorObj.base[0]);
      }
      if (errorObj.cc_number) {
        setNumberError(errorObj.cc_number[0]);
      }

      if (errorObj.cc_expiration_month) {
        setExpirationError(errorObj.cc_expiration_month[0]);
      }

      if (errorObj.cc_expiration_year) {
        setExpirationError(errorObj.cc_expiration_year[0]);
      }

      if (errorObj.cc_csc) {
        setCvvError(errorObj.cc_csc[0]);
      }
      if (errorObj.first_name || errorObj.last_name) {
        const nameErrors = [];
        if (errorObj.first_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.FIRST_NAME}: ${errorObj.first_name[0]}`);
        }
        if (errorObj.last_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.LAST_NAME}: ${errorObj.last_name[0]}`);
        }
        setNameError(nameErrors.join(', '));
      }
    } else {
      setServerError(<FormattedMessage defaultMessage="There was an error processing your payment. Please contact support." />);
    }

    setSubmitting(false);
  }

  function getCouponCodeInput() {
    return (
      <CouponCode
        applyHandler={handleApplyCoupon}
        changeHandler={handleCouponCodeChange}
        errorMessage={couponError}
        expanded={isCouponInputExpanded}
        isSuccess={isCouponApplied}
        removeHandler={handleRemoveCoupon}
        value={couponCode}
      />
    );
  }

  function getFirstAndLastName() {
    const fullName = name.split(' ');
    const billing_first_name = fullName[0];
    const billing_last_name = fullName[fullName.length - 1];

    return { billing_first_name, billing_last_name };
  }

  function noGiftCard() {
    if (!coupon) {
      return true;
    }

    return coupon.type !== GIFT_CARD;
  }

  function submitPayment(params: { amazonPayToken?: string; applePayToken?: string; paypalToken?: string; recaptchaToken?: string }) {
    const { billing_first_name, billing_last_name } = getFirstAndLastName();

    const [cc_expiration_month, cc_expiration_year] = expiration.split(' / ');

    const submissionData = {
      plan: plan.planCode, // this is still used in gifting
      recurly_plan_code: plan.recurlyPlanCode,
      paypal_token: params.paypalToken,
      apple_pay_token: params.applePayToken,
      recaptcha: params.recaptchaToken,
      uid: userId,
      user: {
        user_entered_coupon_code: coupon ? coupon.code : null,
        cc_number: number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: cvv,
        zip: postalCode,
        billing_first_name,
        billing_last_name
      }
    };

    if (handlePayment) {
      handlePayment(submissionData).catch(handlePaymentFormError);
      return;
    }

    function getPaymentMethod() {
      if (params.paypalToken) {
        return PaymentMethod.Paypal;
      }

      if (params.applePayToken) {
        return PaymentMethod.ApplePay;
      }

      return PaymentMethod.CreditCard;
    }

    ServerCommunicationUtil.putApiEndpoint(
      '/pro',
      submissionData,
      () => handlePaymentFormSuccess(currencyCode, coupon, plan, locale, couponCode, webProPurchaseLocation, getPaymentMethod()),
      handlePaymentFormError,
      () => {},
      {
        'X-Language-Locale': locale
      }
    );
  }

  function loadRecaptchaAndSubmit(e?: any, paypalToken?: string, applePayToken?: string, amazonPayToken?: string) {
    if (e) {
      e.preventDefault();
    }

    if (submitting) {
      return;
    }

    setSubmitting(true);

    // feels like this should be hoisted out
    if (!applePayToken && !amazonPayToken && !paypalToken && noGiftCard()) {
      if (
        validate({
          name,
          cvv,
          expiration,
          number,
          postalCode,
          setCvvError,
          setNameError,
          setExpirationError,
          setNumberError,
          setPostalCodeError
        })
      ) {
        setSubmitting(false);
        return;
      }
    }

    if (enableRecaptcha) {
      loadAndExecuteRecaptcha('userPaymentAction', (recaptchaToken: string) => {
        submitPayment({ recaptchaToken, paypalToken, applePayToken, amazonPayToken });
      });
    } else {
      submitPayment({ paypalToken, applePayToken, amazonPayToken });
    }
  }

  function renderServerError() {
    return <div className={serverError ? styles.serverError : ''}>{serverError}</div>;
  }

  function renderExternalPayForm() {
    return (
      <div>
        <div>
          <div className={formStyles.promptHead}>
            <FormattedMessage defaultMessage="Choose your payment method:" />
          </div>
          <div className={formStyles.prompt}>
            <FormattedMessage defaultMessage="By clicking an option below you will be redirected to either PayPal or Apple Pay to complete your purchase." />
          </div>
        </div>
        {isCouponInputShown && getCouponCodeInput()}
        <ExternalPayForm
          amazonPayHandler={(amazonPayToken: string) => loadRecaptchaAndSubmit(undefined, undefined, undefined, amazonPayToken)}
          applePayHandler={handleApplePayClick}
          paypalHandler={handlePaypalClick}
          showAmazonPay={paymentTypes[3].showTab}
          showApplePay={paymentTypes[2].showTab}
          submitting={submitting}
        />
        {renderServerError()}
      </div>
    );
  }

  function renderCreditCardForm() {
    return (
      <form id="payment-form" onSubmit={loadRecaptchaAndSubmit}>
        <CreditCardForm
          changeHandler={handleCreditCardFormChange}
          cvv={cvv}
          errors={{ expiration: expirationError, name: nameError, number: numberError, cvv: cvvError, postalCode: postalCodeError }}
          expiration={expiration}
          isCouponInputExpanded={isCouponInputExpanded}
          isCouponInputShown={isCouponInputShown}
          name={name}
          number={number}
          onSubmit={loadRecaptchaAndSubmit}
          postalCode={postalCode}
          setError={(name, error) => {
            // these are then only two errors set on the credit card form
            switch (name) {
              case 'expiration':
                setExpirationError(error);
                break;
              case 'number':
                setNumberError(error);
                break;
              default:
                throw new Error(`missing credit card form error handling for name '${name}'`);
            }
          }}
          toggleCouponCode={toggleCouponCode}
        />
        {isCouponInputShown && getCouponCodeInput()}
        {renderServerError()}
        <div className={styles.submit}>
          <Button
            disabled={submitting}
            onClick={() => {
              logProPurchaseBuyNowClicked({
                purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
                web_purchase_location: webProPurchaseLocation
              });
            }}
            testId="pro-payment-form-submit"
            text={submitText}
            type="submit"
            variant="primary"
            width="full"
          />
        </div>
      </form>
    );
  }

  // fine print is hidden on the gifting pages
  const footText = !hideFinePrint ? (
    <PaymentFinePrint coupon={coupon} currencyCode={currencyCode} isCouponApplied={isCouponApplied} plan={plan} />
  ) : null;

  return (
    <Wrapper foot={footText} text={{ headText }}>
      <div className={baseStyles.section}>
        <div className={styles.planType}>
          <PlanTypeCard currencyCode={currencyCode} isCouponApplied={isCouponApplied} plan={plan} staticCard coupon={coupon} />
        </div>
        <div className={baseStyles.row}>
          <PaymentTabs
            activeTab={activePaymentTab}
            handler={activePaymentTab => {
              setActivePaymentTab(activePaymentTab);
            }}
            showPayWithCard={showPayWithCard}
            tabs={paymentTypes}
          />
        </div>
        <div className={styles.body}>{activePaymentTab === 0 ? renderCreditCardForm() : renderExternalPayForm()}</div>
      </div>
    </Wrapper>
  );
}
